import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, ExternalLink, X, RotateCcw, Pencil, Check } from 'lucide-react';
import MiloOriginBadge from './MiloOriginBadge';
import { fetchSurfaced, undoSurfaced, editSurfaced } from '../intelligence-notebook/notebookSurfacedApi';

/**
 * MiloNotebookItems — the ONE shared, read-only panel that surfaces Milo notebook-origin
 * records inside a destination tab (Guests / Vendors / Program / Messaging). Used identically
 * everywhere so the treatment is consistent.
 *
 * Hard rules (enforced by what this component can do, not just convention):
 *  - It renders Milo-origin records only. Mutations are limited to: UNDO (delete the
 *    Milo-created record via its governed write-back) and EDIT-IN-PLACE of the record's
 *    primary text (operator-gated server-side, restricted to created_via='milo_notebook'
 *    rows — a human-authored row can never be edited/removed through this panel).
 *  - Every item shows the shared MiloOriginBadge (created-by-Milo + source snippet +
 *    approved/confidence/last-edit) and a "View in Notebook" link back to the source entry.
 *  - Origin filter: All / Milo / Human. Notebook-origin records are always Milo, so the
 *    Human view is empty here by design — the control is consistent platform-wide and ready
 *    for when a destination list mixes origins.
 *
 * Gating: callers render this ONLY when the Intelligence Notebook flag is on, so production
 * tabs are untouched until activation.
 *
 * Props:
 *  kind         — 'guest_note' | 'vendor_followup' | 'program_moment' | 'message_draft'
 *  title        — section heading (e.g. "Milo Notes", "Milo Suggestions")
 *  emptyText    — shown when there are no records
 *  onOpenNotebook(entryId)  — navigate to the Notebook focused on the source entry (optional)
 *  refreshKey   — bump to force a refetch
 */
const GOLD = '#C6A75A';
const INK = '#1E1A14';

function primaryText(item) {
  return item.text || item.title || item.body || '(note)';
}

function secondaryText(item) {
  if (item.kind === 'program_moment') {
    return item.suggested_time ? `Suggested time ${item.suggested_time}` : 'Timing to confirm';
  }
  if (item.kind === 'message_draft') return 'Draft · not sent';
  // operation / finance_entry (and any future kind) carry a server-normalized `secondary`.
  return item.secondary || null;
}

// Default "link back to the Notebook entry": a decoupled window event AdminPage listens
// for (so no nav prop needs threading through the 4 destination components).
function defaultOpenNotebook(entryId) {
  try {
    if (entryId) window.localStorage.setItem('intelligence_notebook_focus_entry', entryId);
    window.dispatchEvent(new CustomEvent('open-intelligence-notebook', { detail: { entryId } }));
  } catch (e) { /* ignore */ }
}

export function MiloNotebookItems({
  kind, title = 'From your Notebook', emptyText = 'Nothing from your Notebook here yet.',
  onOpenNotebook = defaultOpenNotebook, refreshKey = 0,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'milo' | 'human'
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    fetchSurfaced(kind)
      .then((rows) => setItems(Array.isArray(rows) ? rows : []))
      // Inert/pre-activation (route not yet live, or kill-switch off) → just show the
      // empty state, never a scary error in a gated, not-yet-activated panel.
      .catch(() => { setItems([]); })
      .finally(() => setLoading(false));
  }, [kind]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const onUndo = useCallback((item) => {
    if (!item.writeback_proposal_id) return;
    setBusyId(item.id);
    undoSurfaced(item.writeback_proposal_id)
      .then(() => setItems((prev) => prev.filter((x) => x.id !== item.id)))
      .catch(() => { /* leave as is; re-load reflects truth */ load(); })
      .finally(() => setBusyId(null));
  }, [load]);

  const startEdit = useCallback((item) => {
    setEditingId(item.id);
    setDraft(primaryText(item));
  }, []);

  const cancelEdit = useCallback(() => { setEditingId(null); setDraft(''); }, []);

  const saveEdit = useCallback((item) => {
    const text = draft.trim();
    if (!text) return;
    setSavingId(item.id);
    editSurfaced(kind, item.id, text)
      .then(() => {
        // reflect the new primary text locally; secondary/provenance unchanged
        setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, text, title: text, body: text } : x)));
        setEditingId(null);
        setDraft('');
      })
      .catch(() => { /* governance/offline → leave editing open; truth re-syncs on reload */ })
      .finally(() => setSavingId(null));
  }, [draft, kind]);

  // Notebook-origin records are all Milo; the Human view is intentionally empty.
  const visible = filter === 'human' ? [] : items;

  return (
    <div
      data-testid={`milo-notebook-items-${kind}`}
      className="rounded-2xl"
      style={{ background: 'rgba(198,167,90,0.05)', border: '1px solid rgba(198,167,90,0.25)', padding: 14 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginBottom: visible.length || loading ? 10 : 4 }}>
        <div className="flex items-center gap-2">
          <Sparkles size={15} style={{ color: GOLD }} />
          <span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.04em', color: INK }}>{title}</span>
          {!loading && <span style={{ fontSize: 11, color: 'rgba(31,26,20,0.5)' }}>· {items.length}</span>}
        </div>
        {/* Origin filter */}
        <div className="flex items-center gap-0.5 rounded-full p-0.5" style={{ background: 'rgba(31,26,20,0.04)', border: '1px solid rgba(198,167,90,0.25)' }}>
          {[['all', 'All'], ['milo', 'Milo'], ['human', 'Human']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              data-testid={`milo-filter-${k}`}
              style={{
                fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                background: filter === k ? 'rgba(255,255,255,0.92)' : 'transparent',
                color: filter === k ? INK : 'rgba(31,26,20,0.5)',
                border: filter === k ? '1px solid rgba(198,167,90,0.35)' : '1px solid transparent',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={{ fontSize: 12.5, color: 'rgba(31,26,20,0.5)', fontStyle: 'italic' }}>Loading…</div>}
      {!loading && error && (
        <div style={{ fontSize: 12.5, color: 'rgba(31,26,20,0.5)' }}>
          Couldn’t load Notebook items. <button type="button" onClick={load} style={{ color: GOLD, fontWeight: 600 }}>Try again</button>
        </div>
      )}
      {!loading && !error && visible.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'rgba(31,26,20,0.5)', fontStyle: 'italic' }}>
          {filter === 'human' ? 'No human-created items here — these come from your Notebook (Milo).' : emptyText}
        </div>
      )}

      {/* Items (read-only) */}
      <div className="flex flex-col gap-2">
        {!loading && !error && visible.map((item) => {
          const sec = secondaryText(item);
          return (
            <div
              key={item.id}
              data-testid="milo-notebook-item"
              className="rounded-xl flex items-start gap-3"
              style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(198,167,90,0.2)', padding: '10px 12px' }}
            >
              <div className="flex-1 min-w-0">
                {editingId === item.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="milo-edit-input"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(item); if (e.key === 'Escape') cancelEdit(); }}
                      autoFocus
                      className="flex-1 min-w-0 rounded-lg px-2 py-1"
                      style={{ fontSize: 13.5, color: INK, border: '1px solid rgba(198,167,90,0.5)', background: '#fff', outline: 'none' }}
                    />
                    <button
                      type="button" data-testid="milo-edit-save" onClick={() => saveEdit(item)}
                      disabled={savingId === item.id || !draft.trim()} aria-label="Save edit" title="Save"
                      className="shrink-0 rounded-full flex items-center justify-center"
                      style={{ width: 26, height: 26, background: 'rgba(198,167,90,0.18)', border: '1px solid rgba(198,167,90,0.5)', color: '#5e4a18' }}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      type="button" data-testid="milo-edit-cancel" onClick={cancelEdit}
                      aria-label="Cancel edit" title="Cancel"
                      className="shrink-0 rounded-full flex items-center justify-center"
                      style={{ width: 26, height: 26, background: 'rgba(31,26,20,0.04)', border: '1px solid rgba(31,26,20,0.12)', color: 'rgba(31,26,20,0.5)' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: 13.5, color: INK, fontWeight: 500, lineHeight: 1.4 }}>{primaryText(item)}</div>
                )}
                {sec && editingId !== item.id && <div style={{ fontSize: 11.5, color: 'rgba(31,26,20,0.55)', marginTop: 1 }}>{sec}</div>}
                <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 6 }}>
                  <MiloOriginBadge
                    createdBy={item.created_by || 'milo'}
                    sourceLabel="Notebook entry"
                    sourceSnippet={item.notebook_source_snippet}
                    approvedAt={item.notebook_written_at}
                    approvedBy="human"
                    confidence={item.confidence}
                    size="xs"
                    onOpenSource={onOpenNotebook ? () => onOpenNotebook(item.notebook_source_entry_id) : undefined}
                  />
                  {onOpenNotebook && (
                    <button
                      type="button"
                      data-testid="milo-source-link"
                      onClick={() => onOpenNotebook(item.notebook_source_entry_id)}
                      className="inline-flex items-center gap-1"
                      style={{ fontSize: 10.5, fontWeight: 600, color: GOLD }}
                    >
                      View in Notebook <ExternalLink size={11} />
                    </button>
                  )}
                </div>
              </div>
              {editingId !== item.id && (
                <div className="shrink-0 flex items-center gap-1.5">
                  <button
                    type="button"
                    data-testid="milo-edit"
                    onClick={() => startEdit(item)}
                    aria-label="Edit this Milo item"
                    title="Edit"
                    className="rounded-full flex items-center justify-center"
                    style={{
                      width: 26, height: 26, background: 'rgba(31,26,20,0.04)',
                      border: '1px solid rgba(31,26,20,0.12)', color: 'rgba(31,26,20,0.5)', cursor: 'pointer',
                    }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    data-testid="milo-undo"
                    onClick={() => onUndo(item)}
                    disabled={busyId === item.id || !item.writeback_proposal_id}
                    aria-label="Remove this Milo item"
                    title="Remove (undo)"
                    className="rounded-full flex items-center justify-center"
                    style={{
                      width: 26, height: 26, background: 'rgba(31,26,20,0.04)',
                      border: '1px solid rgba(31,26,20,0.12)', color: 'rgba(31,26,20,0.5)',
                      cursor: busyId === item.id ? 'default' : 'pointer',
                    }}
                  >
                    {busyId === item.id ? <RotateCcw size={13} /> : <X size={14} />}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MiloNotebookItems;
